import { useState, useRef, useCallback, useEffect } from "react";
import { nhost } from "@/lib/nhost";
import { withTimeout } from "@/lib/asyncTimeout";

// ── Module-level singletons — shared across all hook instances ──────────────────

let cachedPaystackPublicKey: string | null = null;
let scriptLoadPromise: Promise<void> | null = null;

const getPaystackPublicKey = async (): Promise<{ key: string; error?: string }> => {
  if (cachedPaystackPublicKey) return { key: cachedPaystackPublicKey };
  try {
    const { body } = await nhost.graphql.request({
      query: `query PaystackKey { app_config(where: { key: { _eq: "paystack_public_key" } }, limit: 1) { value } }`,
    });
    const data = body.data as { app_config?: { value: string }[] } | undefined;
    const value = data?.app_config?.[0]?.value;
    if (!value) return { key: "", error: "Paystack public key not found." };
    cachedPaystackPublicKey = value;
    return { key: cachedPaystackPublicKey };
  } catch (e: unknown) {
    return { key: "", error: e instanceof Error ? e.message : "Failed to load payment config" };
  }
};

const loadPaystackScript = (): Promise<void> => {
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (document.getElementById("paystack-script")) {
      // @ts-expect-error Paystack loaded via CDN
      if (window.PaystackPop) resolve();
      else {
        const interval = setInterval(() => {
          // @ts-expect-error Paystack loaded via CDN
          if (window.PaystackPop) { clearInterval(interval); resolve(); }
        }, 50);
        setTimeout(() => { clearInterval(interval); reject(new Error("Timeout")); }, 5000);
      }
      return;
    }
    const script = document.createElement("script");
    script.id = "paystack-script";
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Paystack script."));
    document.body.appendChild(script);
  });
  return scriptLoadPromise;
};

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PaystackPaymentConfig {
  email: string;
  amount: number;
  reference?: string;
  metadata?: Record<string, unknown>;
  onSuccess: (reference: string) => void;
  onClose?: () => void;
  onError: (message: string) => void;
  /** Order details. The server uses these to create the order and decide the price. */
  saveOrderParams?: Record<string, unknown> | null;
}

type CheckoutResponse = {
  success?: boolean;
  reference?: string;
  amount?: number;
  email?: string;
  error?: string;
  momo?: { number: string; name: string; reference_text: string };
};

// 📱 What the MoMo payment window needs (see components/feature/MomoPayModal.tsx)
export type MomoPayRequest = {
  reference: string;
  amount: number;
  momo: { number: string; name: string; reference_text: string };
  onSuccess: (reference: string) => void;
};
export const MOMO_OPEN_EVENT = "mad:open-momo-pay";

// Turn whatever the page gave us into a simple request for the server.
// The server decides the price. The page's own amount is ignored.
function buildCheckoutRequest(config: PaystackPaymentConfig): Record<string, unknown> {
  const meta = (config.metadata || {}) as Record<string, unknown>;
  const sop = (config.saveOrderParams || {}) as Record<string, unknown>;
  const fields = Array.isArray(meta.custom_fields)
    ? (meta.custom_fields as { variable_name?: string; value?: unknown }[])
    : [];
  const field = (name: string) => fields.find((f) => f.variable_name === name)?.value;

  const purposeRaw = String(meta.purpose || sop.purpose || field("purpose") || "");
  const isTopUp = purposeRaw === "wallet_topup" || purposeRaw === "Wallet Top-Up";

  if (isTopUp) {
    return {
      purpose: "wallet_topup",
      amount: Number(field("net_amount") ?? sop.net_amount ?? 0),
      customer_email: config.email,
      recipient_phone: sop.recipient_phone || meta.recipient_phone || "",
    };
  }

  return {
    purpose: "data_purchase",
    network: sop.network || meta.network || field("network"),
    package: sop.package_size || meta.package_size || sop.package_name || meta.package || field("package"),
    recipient_phone: sop.recipient_phone || meta.recipient_phone || field("recipient_phone"),
    recipient_name: sop.recipient_name || meta.recipient_name,
    customer_email: sop.customer_email || config.email,
    ref_code: meta.ref_code || field("ref_code") || sop.ref_code,
    verification_status: sop.verification_status || meta.verification_status,
    verification_checked_at: sop.verification_checked_at || meta.verification_checked_at,
  };
}

function serverErrorMessage(e: unknown): string {
  const body = (e as { body?: { error?: unknown } })?.body;
  if (body && typeof body.error === "string") return body.error;
  return e instanceof Error ? e.message : "Payment failed. Please try again.";
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function usePaystackPayment() {
  const [loading, setLoading] = useState(false);
  const scriptReadyRef = useRef(false);

  // Preload key and script as soon as the consumer component mounts
  useEffect(() => {
    let cancelled = false;
    getPaystackPublicKey();
    loadPaystackScript().then(() => {
      if (!cancelled) scriptReadyRef.current = true;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const triggerPayment = useCallback(async (config: PaystackPaymentConfig) => {
    setLoading(true);

    try {
      // 1. Ensure Paystack public key is loaded (with 8s timeout)
      let publicKey = cachedPaystackPublicKey;
      if (!publicKey) {
        const { key, error: keyError } = await withTimeout(getPaystackPublicKey(), 8000, "Paystack key");
        if (keyError || !key) {
          config.onError(keyError || "Could not load payment system.");
          setLoading(false);
          return;
        }
        publicKey = key;
      }

      // 2. Ensure Paystack script is loaded (with 8s timeout)
      if (!scriptReadyRef.current) {
        await withTimeout(loadPaystackScript(), 8000, "Paystack script");
        scriptReadyRef.current = true;
      }

      // @ts-expect-error PaystackPop loaded via CDN
      if (!window.PaystackPop) {
        config.onError("Paystack failed to load. Please refresh.");
        setLoading(false);
        return;
      }

      // 3. Ask the server to create the order and decide the price
      let checkout: CheckoutResponse;
      try {
        const res = await withTimeout(
          nhost.functions.post("/start-checkout", buildCheckoutRequest(config)),
          10000,
          "Checkout"
        );
        checkout = res.body as CheckoutResponse;
      } catch (e: unknown) {
        config.onError(serverErrorMessage(e));
        setLoading(false);
        return;
      }

      if (!checkout?.success || !checkout.reference || !checkout.amount) {
        config.onError(checkout?.error || "Could not start payment. Please try again.");
        setLoading(false);
        return;
      }

      const reference = checkout.reference;

      // 4. Open Paystack popup with the SERVER's price and reference
      // @ts-expect-error PaystackPop loaded via CDN
      const handler = window.PaystackPop.setup({
        key: publicKey,
        email: checkout.email || config.email,
        amount: Math.round(checkout.amount * 100),
        currency: "GHS",
        ref: reference,
        metadata: config.metadata || {},
        callback: function (response: { reference: string }) {
          // Ring the kitchen right away so delivery starts fast
          nhost.functions.post("/verify-payment", { reference: response.reference }).catch(() => {});
          config.onSuccess(response.reference);
        },
        onClose: function () {
          setLoading(false);
          config.onClose?.();
        },
      });
      handler.openIframe();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Payment failed. Please try again.";
      config.onError(msg.includes("timed out") ? "Connection is slow. Check your internet." : msg);
      setLoading(false);
    }
  }, []);

  // 📱 MoMo direct: the server creates the order with the MoMo price (+2%),
  // then the MoMo payment window opens with our number and the reference.
  const triggerMomoPayment = useCallback(async (config: PaystackPaymentConfig) => {
    setLoading(true);
    try {
      let checkout: CheckoutResponse;
      try {
        const res = await withTimeout(
          nhost.functions.post("/start-checkout", { ...buildCheckoutRequest(config), payment_method: "momo" }),
          10000,
          "Checkout"
        );
        checkout = res.body as CheckoutResponse;
      } catch (e: unknown) {
        config.onError(serverErrorMessage(e));
        return;
      }
      if (!checkout?.success || !checkout.reference || !checkout.amount || !checkout.momo) {
        config.onError(checkout?.error || "Could not start payment. Please try again.");
        return;
      }
      const detail: MomoPayRequest = {
        reference: checkout.reference,
        amount: checkout.amount,
        momo: checkout.momo,
        onSuccess: config.onSuccess,
      };
      window.dispatchEvent(new CustomEvent(MOMO_OPEN_EVENT, { detail }));
      config.onClose?.();
    } catch (e: unknown) {
      config.onError(e instanceof Error ? e.message : "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  return { triggerPayment, triggerMomoPayment, loading };
}